import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { applyTypography, BaseButton, Input, useT } from '@repo/common';

import StarRating from '../../../Kpathport/Workshop/StarRating';
import {
  deleteMyWorkshopReview,
  getWorkshopReviews,
  submitWorkshopReview,
} from '../../../../api/workshop/workshopReview.api';

const MAX_LENGTH = 1000;

/**
 * 워크숍 후기 작성·수정.
 *
 * <p>이미 쓴 후기가 있으면 불러와 채운 뒤 수정으로 동작한다. 서버도 upsert 라
 * 여기서 새 글인지 수정인지 구분해 보낼 필요는 없고, 버튼 문구만 바꾼다.
 *
 * @param {function} [onSaved] 저장이면 true, 삭제면 false 로 호출한다
 */
const WriteReviewModal = ({
  isOpen,
  workshopId,
  workshopTitle,
  onClose,
  onSaved,
}) => {
  const t = useT();
  const [rating, setRating] = useState(0);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  const [error, setError] = useState('');

  /*
   * 뒤 목록이 스크롤되지 않게 body 를 잠근다.
   * 공용 useScrollLock 은 document 전체의 휠을 막아 모달 안까지 못 굴리므로 쓰지 않는다.
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !workshopId) return undefined;

    let active = true;
    setLoading(true);
    setError('');
    getWorkshopReviews(workshopId)
      .then((data) => {
        if (!active) return;
        const mine = data?.myReview;
        setHasExisting(Boolean(mine));
        setRating(mine?.rating ?? 0);
        setContent(mine?.content ?? '');
      })
      .catch(() => {
        // 못 불러와도 새로 쓰는 건 가능하다
        if (active) setHasExisting(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, workshopId]);

  if (!isOpen) return null;

  const canSubmit = rating > 0 && content.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await submitWorkshopReview(workshopId, {
        rating,
        content: content.trim(),
      });
      onSaved?.(true);
      onClose?.();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.msg ||
          t("Couldn't save your review.", '후기를 저장하지 못했습니다.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await deleteMyWorkshopReview(workshopId);
      onSaved?.(false);
      onClose?.();
    } catch {
      setError(
        t("Couldn't delete your review.", '후기를 삭제하지 못했습니다.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    /*
     * 포털은 DOM 상 body 아래지만 React 이벤트는 컴포넌트 트리를 타고 올라간다.
     * 감싸지 않으면 모달 클릭이 이 모달을 띄운 예약 행의 onClick 까지 도달해
     * 워크숍 상세로 이동해 버린다. 여기서 한 번 끊는다.
     */
    <div onClick={(e) => e.stopPropagation()}>
      <div
        className='fixed inset-0 z-[9998] bg-surface-black40'
        onClick={onClose}
      />
      <div className='fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6'>
        <div
          className='flex max-h-[85vh] w-full max-w-[520px] flex-col overflow-y-auto rounded-[24px] bg-grayscale-gray00 p-6'
          onClick={(e) => e.stopPropagation()}
        >
          <p className={`${applyTypography('b18Strong')} text-text-primary`}>
            {hasExisting
              ? t('Edit your review', '후기 수정')
              : t('Leave a review', '후기 남기기')}
          </p>
          {workshopTitle && (
            <p
              className={`mt-1 truncate ${applyTypography('b13')} text-text-placeholder`}
            >
              {workshopTitle}
            </p>
          )}

          {loading ? (
            <div className='mt-6 h-[180px] animate-pulse rounded-xl bg-grayscale-gray01' />
          ) : (
            <>
              <div className='mt-6 flex flex-col items-center gap-2'>
                <StarRating value={rating} onChange={setRating} size='l' />
                <p
                  className={`${applyTypography('b13')} text-text-placeholder`}
                >
                  {rating > 0
                    ? t(`${rating} out of 5`, `5점 만점에 ${rating}점`)
                    : t('Tap to rate', '별을 눌러 점수를 매겨 주세요')}
                </p>
              </div>

              <div className='mt-5'>
                <Input
                  as='textarea'
                  label=''
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={t(
                    'How was the workshop? Your review helps other guests.',
                    '워크숍은 어떠셨나요? 다른 참가자에게 큰 도움이 됩니다.',
                  )}
                  maxLength={MAX_LENGTH}
                  textareaMinHeight='140px'
                  textareaRows={5}
                />
              </div>
            </>
          )}

          {error && (
            <p className={`mt-3 ${applyTypography('b12')} text-red-600`}>
              {error}
            </p>
          )}

          <div className='mt-6 flex items-center justify-between gap-2'>
            {hasExisting ? (
              <BaseButton
                type='text'
                size='m'
                htmlType='button'
                hideIcon
                typo={false}
                isDisabled={submitting}
                onClick={handleDelete}
                className='h-auto min-w-0 rounded-lg border-0 bg-transparent px-3 py-2 typo-b14Strong text-red-600 hover:bg-red-50 hover:text-red-600'
              >
                {t('Delete', '삭제')}
              </BaseButton>
            ) : (
              <span />
            )}

            <div className='flex items-center gap-2'>
              <BaseButton
                type='text'
                size='m'
                htmlType='button'
                hideIcon
                typo={false}
                onClick={onClose}
              >
                {t('Cancel', '취소')}
              </BaseButton>
              <BaseButton
                type='primary'
                size='m'
                hideIcon
                width='120px'
                isDisabled={!canSubmit}
                onClick={handleSubmit}
              >
                {hasExisting ? t('Update', '수정') : t('Post', '등록')}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WriteReviewModal;
